import { ISaSmsPlanCredits } from '@interfaces/sa-sms';
import { convertPrice } from '@utils/resource';
import { Button, Card, Form, InputNumber, notification, Table } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Props = { plans: ISaSmsPlanCredits[]; canEdit: boolean; onChanged: () => void };

/** Included monthly SMS credits per Marketing / CRM plan. Saving re-pushes the entitlement of the affected tenants (async). */
export const SmsPlanCreditsEditor: React.FC<Props> = ({ plans, canEdit, onChanged }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [editing, setEditing] = useState<ISaSmsPlanCredits | null>(null);

    const confirm = async ({
        reason,
        smsIncludedCredits,
    }: {
        reason: string;
        smsIncludedCredits: number;
    }) => {
        if (!editing) return;
        const out = await send<{ affectedTenants: number }>(
            'put',
            `/settings/plans/${editing.id}/included-credits`,
            { smsIncludedCredits, reason },
        );
        notification.success({
            message: t('settings.planSaved', { count: out?.affectedTenants ?? 0 }),
        });
        setEditing(null);
        onChanged();
    };

    return (
        <Card title={t('settings.planCredits')}>
            <Table<ISaSmsPlanCredits>
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={plans}
            >
                <Table.Column<ISaSmsPlanCredits>
                    title={t('settings.app')}
                    dataIndex="applicationCode"
                />
                <Table.Column<ISaSmsPlanCredits> title={t('settings.plan')} dataIndex="title" />
                <Table.Column<ISaSmsPlanCredits>
                    title={t('settings.price')}
                    align="right"
                    render={(_, p) => convertPrice(p.pricing)}
                />
                <Table.Column<ISaSmsPlanCredits>
                    title={t('settings.includedCredits')}
                    align="right"
                    render={(_, p) => p.smsIncludedCredits.toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsPlanCredits>
                    title=""
                    render={(_, p) =>
                        canEdit && (
                            <Button size="small" onClick={() => setEditing(p)}>
                                {t('actions.edit')}
                            </Button>
                        )
                    }
                />
            </Table>
            <SaSmsReasonModal<{ smsIncludedCredits: number }>
                open={!!editing}
                title={editing ? `${editing.applicationCode} - ${editing.title}` : ''}
                warning={t('settings.planCreditsWarning')}
                initialValues={{ smsIncludedCredits: editing?.smsIncludedCredits }}
                onCancel={() => setEditing(null)}
                onConfirm={confirm}
            >
                <Form.Item
                    name="smsIncludedCredits"
                    label={t('settings.includedCredits')}
                    rules={[{ required: true }]}
                >
                    <InputNumber min={0} max={1000000} precision={0} style={{ width: '100%' }} />
                </Form.Item>
            </SaSmsReasonModal>
        </Card>
    );
};
